/**
 * Crew route — Cyberpunk 2077 sub-agents grid.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  Users,
  Plus,
  Pencil,
  Zap,
  Stethoscope,
  Wrench,
  Guitar,
  Briefcase,
  Shield,
  Compass,
  Newspaper,
  Cpu,
  Sparkles,
  X,
  Loader2,
  Plug,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { CorpoCard } from "../components/ui/CorpoCard";
import { CRTHeader } from "../components/ui/CRTHeader";
import { GlyphButton } from "../components/ui/GlyphButton";
import { HexBadge } from "../components/ui/HexBadge";

interface Agent {
  id: string;
  name: string;
  displayName: string;
  description: string;
  role: string;
  model: string;
  effort: string;
  maxBudgetUsd: number | null;
  icon: string;
  color: string;
  isBuiltin: boolean;
}

interface McpServer {
  id: string;
  name: string;
  displayName: string;
  description: string;
  enabled: boolean;
  icon: string;
  color: string | null;
}

interface AgentsResponse {
  agents: Agent[];
}

// Icon map for lucide icons
const iconMap: Record<string, React.ElementType> = {
  Zap,
  Stethoscope,
  Wrench,
  Guitar,
  Briefcase,
  Shield,
  Compass,
  Newspaper,
  Cpu,
  Sparkles,
};

function AgentIcon({ name, className, style }: { name: string; className?: string; style?: React.CSSProperties }) {
  const IconComponent = iconMap[name] ?? Cpu;
  return <IconComponent className={className} style={style} />;
}

function AgentCard({
  agent,
  onInvoke,
  onEdit,
  isExpanded,
  onToggleExpand,
  allMcpServers,
}: {
  agent: Agent;
  onInvoke: () => void;
  onEdit?: () => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  allMcpServers: McpServer[];
}) {
  const queryClient = useQueryClient();

  // Fetch MCP servers attached to this agent (only when expanded)
  const { data: attachedData } = useQuery<{ servers: McpServer[] }>({
    queryKey: ["agent-mcp-servers", agent.id],
    queryFn: async () => {
      // We'll use the MCP servers endpoint to find which are attached
      const res = await fetch(`/api/mcp/servers`);
      const data = await res.json();
      // Then for each server, check if this agent uses it
      const attached: McpServer[] = [];
      for (const server of data.servers) {
        const agentsRes = await fetch(`/api/mcp/servers/${server.id}/agents`);
        const agentsData = await agentsRes.json();
        if (agentsData.agents?.some((a: { id: string }) => a.id === agent.id)) {
          attached.push(server);
        }
      }
      return { servers: attached };
    },
    enabled: isExpanded,
  });

  const attachMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const res = await fetch(`/api/mcp/agents/${agent.id}/attach/${serverId}`, {
        method: "POST",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mcp-servers", agent.id] });
    },
  });

  const detachMutation = useMutation({
    mutationFn: async (serverId: string) => {
      const res = await fetch(`/api/mcp/agents/${agent.id}/detach/${serverId}`, {
        method: "DELETE",
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-mcp-servers", agent.id] });
    },
  });

  const attachedServerIds = new Set(attachedData?.servers?.map((s) => s.id) ?? []);

  const handleToggleMcp = (serverId: string) => {
    if (attachedServerIds.has(serverId)) {
      detachMutation.mutate(serverId);
    } else {
      attachMutation.mutate(serverId);
    }
  };

  return (
    <CorpoCard
      className="relative cursor-pointer"
      accent
      style={{
        borderLeftColor: agent.color,
        borderLeftWidth: "3px",
      } as React.CSSProperties}
      onClick={onToggleExpand}
    >
      {/* Icon + Name */}
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-12 h-12 flex items-center justify-center rounded-sm"
          style={{
            backgroundColor: `${agent.color}20`,
            border: `1px solid ${agent.color}`,
          }}
        >
          <AgentIcon
            name={agent.icon}
            className="w-6 h-6"
            style={{ color: agent.color } as React.CSSProperties}
          />
        </div>
        <div className="flex-1">
          <h3
            className="font-bold text-lg uppercase tracking-wide"
            style={{ color: agent.color }}
          >
            {agent.displayName}
          </h3>
          <p className="text-xs text-[var(--bw-muted)]">{agent.role}</p>
        </div>
        <div className="flex items-center">
          {isExpanded ? (
            <ChevronDown size={18} className="text-[var(--bw-muted)]" />
          ) : (
            <ChevronRight size={18} className="text-[var(--bw-muted)]" />
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-sm text-[var(--bw-fg)] mb-4 line-clamp-2">
        {agent.description}
      </p>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-4">
        <HexBadge variant="primary">{agent.model.toUpperCase()}</HexBadge>
        <HexBadge variant="secondary">{agent.effort}</HexBadge>
        {agent.maxBudgetUsd && (
          <HexBadge variant="warn">${agent.maxBudgetUsd.toFixed(2)}</HexBadge>
        )}
        {agent.isBuiltin && (
          <HexBadge variant="muted">BUILTIN</HexBadge>
        )}
        {attachedServerIds.size > 0 && (
          <HexBadge variant="secondary">
            <Plug size={10} className="mr-1" />
            {attachedServerIds.size} MCP
          </HexBadge>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        <GlyphButton
          variant="primary"
          size="sm"
          onClick={onInvoke}
          className="flex-1"
        >
          INVOKE
        </GlyphButton>
        <GlyphButton
          variant="ghost"
          size="sm"
          onClick={onEdit}
          disabled={agent.isBuiltin}
          icon={<Pencil size={14} />}
        >
          EDIT
        </GlyphButton>
      </div>

      {/* Expanded: MCP Server selection */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-[var(--bw-border)]" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-2 mb-3">
            <Plug size={14} className="text-[var(--bw-accent)]" />
            <span className="text-xs text-[var(--bw-muted)] uppercase">MCP Servers</span>
          </div>

          {allMcpServers.length === 0 ? (
            <p className="text-xs text-[var(--bw-muted)]">No MCP servers available</p>
          ) : (
            <div className="space-y-2">
              {allMcpServers.map((server) => (
                <label
                  key={server.id}
                  className="flex items-center gap-3 p-2 bg-[var(--bw-surface)] rounded cursor-pointer hover:bg-[var(--bw-surface-2)]"
                >
                  <input
                    type="checkbox"
                    checked={attachedServerIds.has(server.id)}
                    onChange={() => handleToggleMcp(server.id)}
                    disabled={attachMutation.isPending || detachMutation.isPending || !server.enabled}
                    className="w-4 h-4 accent-[var(--bw-accent)]"
                  />
                  <div className="flex-1">
                    <span
                      className="text-sm font-medium"
                      style={{ color: server.enabled ? (server.color ?? "var(--bw-fg)") : "var(--bw-muted)" }}
                    >
                      {server.displayName}
                    </span>
                    {!server.enabled && (
                      <span className="ml-2 text-xs text-[var(--bw-warn)]">(disabled)</span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </CorpoCard>
  );
}

interface CreateAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateAgentModal({ isOpen, onClose, onCreated }: CreateAgentModalProps) {
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    role: "",
    system_prompt: "",
    model: "sonnet",
    effort: "medium",
    max_budget_usd: "",
    icon: "Cpu",
    color: "#00f0ff",
  });
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/agents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          max_budget_usd: formData.max_budget_usd
            ? parseFloat(formData.max_budget_usd)
            : undefined,
          allowed_tools: ["Read", "Write", "Edit", "Glob", "Grep", "WebSearch"],
          add_dirs: [],
        }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Failed to create agent");
      }
      return res.json();
    },
    onSuccess: () => {
      onCreated();
      onClose();
      setFormData({
        name: "",
        display_name: "",
        description: "",
        role: "",
        system_prompt: "",
        model: "sonnet",
        effort: "medium",
        max_budget_usd: "",
        icon: "Cpu",
        color: "#00f0ff",
      });
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Unknown error");
    },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="w-full max-w-lg mx-4 bg-[var(--bw-bg)] border border-[var(--bw-border)] p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <CRTHeader text="Crear Agente" level={3} />
          <button
            onClick={onClose}
            className="text-[var(--bw-muted)] hover:text-[var(--bw-fg)]"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-2 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)] text-sm">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Nombre (slug)
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })
                }
                placeholder="my-agent"
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Nombre Display
              </label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) =>
                  setFormData({ ...formData, display_name: e.target.value })
                }
                placeholder="My Agent"
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs text-[var(--bw-muted)] mb-1">
              Rol
            </label>
            <input
              type="text"
              value={formData.role}
              onChange={(e) =>
                setFormData({ ...formData, role: e.target.value })
              }
              placeholder="Code / Research / ..."
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs text-[var(--bw-muted)] mb-1">
              Descripción
            </label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Breve descripción del agente"
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-xs text-[var(--bw-muted)] mb-1">
              System Prompt
            </label>
            <textarea
              value={formData.system_prompt}
              onChange={(e) =>
                setFormData({ ...formData, system_prompt: e.target.value })
              }
              placeholder="Eres un agente especializado en..."
              rows={4}
              className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] resize-none focus:border-[var(--bw-accent)] focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Model
              </label>
              <select
                value={formData.model}
                onChange={(e) =>
                  setFormData({ ...formData, model: e.target.value })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              >
                <option value="haiku">Haiku</option>
                <option value="sonnet">Sonnet</option>
                <option value="opus">Opus</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Effort
              </label>
              <select
                value={formData.effort}
                onChange={(e) =>
                  setFormData({ ...formData, effort: e.target.value })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="max">Max</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Budget ($)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.max_budget_usd}
                onChange={(e) =>
                  setFormData({ ...formData, max_budget_usd: e.target.value })
                }
                placeholder="5.00"
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] font-mono focus:border-[var(--bw-accent)] focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Icon
              </label>
              <select
                value={formData.icon}
                onChange={(e) =>
                  setFormData({ ...formData, icon: e.target.value })
                }
                className="w-full bg-[var(--bw-surface)] border border-[var(--bw-border)] px-3 py-2 text-sm text-[var(--bw-fg)] focus:border-[var(--bw-accent)] focus:outline-none"
              >
                {Object.keys(iconMap).map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-[var(--bw-muted)] mb-1">
                Color
              </label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) =>
                  setFormData({ ...formData, color: e.target.value })
                }
                className="w-full h-[38px] bg-[var(--bw-surface)] border border-[var(--bw-border)] cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <GlyphButton
            variant="primary"
            onClick={() => createMutation.mutate()}
            disabled={
              createMutation.isPending ||
              !formData.name ||
              !formData.display_name ||
              !formData.system_prompt
            }
            className="flex-1"
          >
            {createMutation.isPending ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              "CREAR AGENTE"
            )}
          </GlyphButton>
          <GlyphButton variant="ghost" onClick={onClose}>
            CANCELAR
          </GlyphButton>
        </div>
      </div>
    </div>
  );
}

export function CrewRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<AgentsResponse>({
    queryKey: ["agents"],
    queryFn: async () => {
      const res = await fetch("/api/agents");
      return res.json();
    },
  });

  // Fetch all MCP servers for attachment UI
  const { data: mcpData } = useQuery<{ servers: McpServer[] }>({
    queryKey: ["mcp-servers"],
    queryFn: async () => {
      const res = await fetch("/api/mcp/servers");
      return res.json();
    },
  });

  const handleInvoke = (agent: Agent) => {
    navigate({ to: "/chat", search: { agent: agent.name, session: undefined } });
  };

  const handleEdit = (agent: Agent) => {
    // TODO: Edit modal for custom agents
    console.log("Edit agent:", agent.id);
  };

  const agents = data?.agents ?? [];
  const allMcpServers = mcpData?.servers ?? [];

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={24} className="text-[var(--bw-accent)]" />
          <CRTHeader text="Crew" level={2} />
        </div>
        <GlyphButton
          variant="secondary"
          onClick={() => setShowCreateModal(true)}
          icon={<Plus size={14} />}
        >
          CREAR AGENTE
        </GlyphButton>
      </div>

      <p className="text-sm text-[var(--bw-muted)]">
        Click on an agent to expand and configure MCP servers.
      </p>

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[var(--bw-accent)]" size={32} />
        </div>
      )}

      {error && (
        <div className="p-4 bg-[var(--bw-danger)]/10 border border-[var(--bw-danger)] text-[var(--bw-danger)]">
          Error cargando agentes
        </div>
      )}

      {!isLoading && !error && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onInvoke={() => handleInvoke(agent)}
              onEdit={() => handleEdit(agent)}
              isExpanded={expandedAgentId === agent.id}
              onToggleExpand={() =>
                setExpandedAgentId(expandedAgentId === agent.id ? null : agent.id)
              }
              allMcpServers={allMcpServers}
            />
          ))}

          {agents.length === 0 && (
            <div className="col-span-full text-center py-12 text-[var(--bw-muted)]">
              No hay agentes disponibles
            </div>
          )}
        </div>
      )}

      <CreateAgentModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => queryClient.invalidateQueries({ queryKey: ["agents"] })}
      />
    </div>
  );
}
